import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Avatar as MuiAvatar,
} from '@material-ui/core';
import { Close as CloseIcon, PersonAdd, RemoveCircleOutline } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { SelectVolunteersDialog } from '../../../common/select-volunteers/SelectVolunteersDialog';
import { GetSelectVolunteerFragment } from '../../../common/select-volunteers/__generated__/GetSelectVolunteerFragment';
import { FallbackAvatar } from '../user-profile/components/Avatar';
import { useSession } from '../../../global/auth/useSession';
import { useApiUrl } from '../../../global/config/useApiUrl';

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || 'https://chat.volaby.org';

interface ProfileInfo {
  preferredName: string;
  lastName: string;
  avatarUrl: string | null;
}

interface MemberInfo {
  volunteerId: string;
  role: string;
  manuallyAdded?: boolean;
  profile?: ProfileInfo;
}

interface ManageMembersDialogProps {
  open: boolean;
  conversationId: string;
  chatToken: string | null;
  onClose: () => void;
  onUpdated: () => void;
  readOnly?: boolean;
}

export const ManageMembersDialog = ({
  open,
  conversationId,
  chatToken,
  onClose,
  onUpdated,
  readOnly = false,
}: ManageMembersDialogProps) => {
  const { user } = useSession();
  const apiUrl = useApiUrl();

  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [pendingAdds, setPendingAdds] = useState<GetSelectVolunteerFragment[]>([]);
  const [pendingRemoves, setPendingRemoves] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const loadMembers = useCallback(async () => {
    if (!chatToken) return;
    try {
      const res = await fetch(`${CHAT_API_URL}/api/conversations/${conversationId}`, {
        headers: { Authorization: `JWT ${chatToken}` },
      });
      const data = await res.json();
      const participantIds = (data.participants || []).map(
        (p: { volunteerId: string; role: string; manuallyAdded?: boolean }) => ({
          volunteerId: p.volunteerId,
          role: p.role,
          manuallyAdded: p.manuallyAdded,
        })
      );

      if (participantIds.length > 0 && user?.token) {
        const profileRes = await fetch(`${apiUrl}/vm/chat/profiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `JWT ${user.token}` },
          body: JSON.stringify({ volunteerIds: participantIds.map((p: MemberInfo) => p.volunteerId) }),
        });
        const profileData = await profileRes.json();
        const profileMap: Record<string, ProfileInfo> = {};
        (profileData.data?.profiles || []).forEach((p: ProfileInfo & { volunteerId: string }) => {
          profileMap[p.volunteerId] = p;
        });

        setMembers(participantIds.map((p: MemberInfo) => ({ ...p, profile: profileMap[p.volunteerId] })));
      } else {
        setMembers(participantIds);
      }
    } catch (err) {
      console.error('Failed to load members:', err);
    } finally {
      setLoading(false);
    }
  }, [conversationId, chatToken, user?.token, apiUrl]);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setPendingAdds([]);
      setPendingRemoves([]);
      loadMembers();
    }
  }, [open, loadMembers]);

  const handleAddVolunteers = (volunteers: GetSelectVolunteerFragment[]) => {
    const existingIds = new Set(members.map((m) => m.volunteerId));
    const newVols = volunteers.filter((v) => !existingIds.has(v.volunteerId));
    setPendingAdds((prev) => [...prev, ...newVols]);
    setShowAddDialog(false);
  };

  const handleRemoveMember = (volunteerId: string) => {
    setPendingRemoves((prev) => [...prev, volunteerId]);
  };

  const handleUndoRemove = (volunteerId: string) => {
    setPendingRemoves((prev) => prev.filter((id) => id !== volunteerId));
  };

  const handleUndoAdd = (volunteerId: string) => {
    setPendingAdds((prev) => prev.filter((v) => v.volunteerId !== volunteerId));
  };

  const handleSubmit = async () => {
    if (!user?.token) return;
    if (pendingAdds.length === 0 && pendingRemoves.length === 0) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      await fetch(`${apiUrl}/vm/chat/channel/update-members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `JWT ${user.token}` },
        body: JSON.stringify({
          conversationId,
          addVolunteerIds: pendingAdds.map((v) => v.volunteerId),
          removeVolunteerIds: pendingRemoves,
        }),
      });
      onUpdated();
      onClose();
    } catch (err) {
      console.error('Failed to update members:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (name: string) =>
    name
      .split(/[\s]+/)
      .map((w) => w[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();

  const hasChanges = pendingAdds.length > 0 || pendingRemoves.length > 0;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Grid container justify='space-between' alignItems='flex-start'>
            <Grid item>
              <Typography variant='h6'>Manage Members</Typography>
              <Typography variant='body2' color='textSecondary'>
                {readOnly
                  ? 'Add additional members. Auto-managed members are locked.'
                  : 'Add or remove channel members'}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton aria-label='Close' color='inherit' onClick={onClose}>
                <CloseIcon color='action' />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box display='flex' justifyContent='center' p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography variant='subtitle2'>
                  Current Members ({members.length - pendingRemoves.length + pendingAdds.length})
                </Typography>
                <TabletButton
                  variant='text'
                  color='primary'
                  onClick={() => setShowAddDialog(true)}
                  startIcon={<PersonAdd />}
                >
                  Add
                </TabletButton>
              </Box>

              <List dense>
                {members
                  .filter((m) => !pendingRemoves.includes(m.volunteerId))
                  .map((member) => (
                    <ListItem key={member.volunteerId}>
                      <ListItemAvatar>
                        {member.profile?.avatarUrl ? (
                          <MuiAvatar src={member.profile.avatarUrl} style={{ width: 36, height: 36 }} />
                        ) : (
                          <FallbackAvatar
                            initials={
                              member.profile
                                ? getInitials(`${member.profile.preferredName} ${member.profile.lastName}`)
                                : '?'
                            }
                            size={36}
                          />
                        )}
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          member.profile
                            ? `${member.profile.preferredName} ${member.profile.lastName}`
                            : member.volunteerId
                        }
                        secondary={member.role === 'admin' ? 'Admin' : undefined}
                      />
                      {member.role !== 'admin' && (readOnly ? member.manuallyAdded : true) && (
                        <ListItemSecondaryAction>
                          <IconButton edge='end' size='small' onClick={() => handleRemoveMember(member.volunteerId)}>
                            <RemoveCircleOutline style={{ color: '#ef5350', fontSize: 20 }} />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}

                {pendingRemoves.length > 0 && (
                  <>
                    <Box mt={1} mb={0.5}>
                      <Typography variant='caption' style={{ color: '#ef5350', fontWeight: 600 }}>
                        Removing
                      </Typography>
                    </Box>
                    {members
                      .filter((m) => pendingRemoves.includes(m.volunteerId))
                      .map((member) => (
                        <ListItem key={member.volunteerId} style={{ opacity: 0.5 }}>
                          <ListItemAvatar>
                            <FallbackAvatar
                              initials={
                                member.profile
                                  ? getInitials(`${member.profile.preferredName} ${member.profile.lastName}`)
                                  : '?'
                              }
                              size={36}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              member.profile
                                ? `${member.profile.preferredName} ${member.profile.lastName}`
                                : member.volunteerId
                            }
                            secondary='Will be removed'
                          />
                          <ListItemSecondaryAction>
                            <TabletButton variant='text' onClick={() => handleUndoRemove(member.volunteerId)}>
                              Undo
                            </TabletButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                  </>
                )}

                {pendingAdds.length > 0 && (
                  <>
                    <Box mt={1} mb={0.5}>
                      <Typography variant='caption' style={{ color: '#4caf50', fontWeight: 600 }}>
                        Adding
                      </Typography>
                    </Box>
                    {pendingAdds.map((vol) => (
                      <ListItem key={vol.volunteerId}>
                        <ListItemAvatar>
                          {vol.profile.avatarUrl ? (
                            <MuiAvatar src={vol.profile.avatarUrl} style={{ width: 36, height: 36 }} />
                          ) : (
                            <FallbackAvatar
                              initials={getInitials(`${vol.profile.preferredName} ${vol.profile.lastName}`)}
                              size={36}
                            />
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${vol.profile.preferredName} ${vol.profile.lastName}`}
                          secondary='Will be added'
                        />
                        <ListItemSecondaryAction>
                          <TabletButton variant='text' onClick={() => handleUndoAdd(vol.volunteerId)}>
                            Undo
                          </TabletButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Box padding={2} display='flex' justifyContent='flex-end' width='100%'>
            <TabletButton variant='text' onClick={onClose} style={{ marginRight: 8 }}>
              Cancel
            </TabletButton>
            <TabletButton
              variant='contained'
              color='primary'
              onClick={handleSubmit}
              disabled={!hasChanges || submitting}
            >
              {submitting ? <CircularProgress size={20} /> : 'Save Changes'}
            </TabletButton>
          </Box>
        </DialogActions>
      </Dialog>

      {showAddDialog && (
        <SelectVolunteersDialog
          open={showAddDialog}
          activity={{ activityId: '', name: '', program: { programId: '', name: '' } }}
          enableGroupByActivity={false}
          enableOrgGroup
          ignoredVolunteerIds={[
            ...members.filter((m) => !pendingRemoves.includes(m.volunteerId)).map((m) => m.volunteerId),
            ...pendingAdds.map((v) => v.volunteerId),
          ]}
          handleClose={() => setShowAddDialog(false)}
          handleAdd={handleAddVolunteers}
        />
      )}
    </>
  );
};
